import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  ref: "container",
  class: "explore-outer hide-scrollbar-mobile"
}
const _hoisted_2 = { class: "title" }
const _hoisted_3 = { class: "link-list" }

import { ref, useTemplateRef, reactive, onMounted, markRaw, watch } from 'vue';
import type { Component } from 'vue';

import Searchbar from '@components/header/Searchbar.vue';
import ClusterHList from '@components/ClusterHList.vue';
import ExploreButton from '@components/ExploreButton.vue';

import FolderIcon from 'vue-material-design-icons/Folder.vue';
import StarIcon from 'vue-material-design-icons/Star.vue';
import VideoIcon from 'vue-material-design-icons/PlayCircle.vue';
import PanoramaIcon from 'vue-material-design-icons/PanoramaVariant.vue';
import ArchiveIcon from 'vue-material-design-icons/PackageDown.vue';
import CalendarIcon from 'vue-material-design-icons/Calendar.vue';
import MapIcon from 'vue-material-design-icons/Map.vue';
import CogIcon from 'vue-material-design-icons/Cog.vue';

import { translate as t } from '@services/l10n';
import { config } from '@services/user-config';
import { windowDims } from '@services/viewport';
import { useRouteState } from '@services/route-state';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as dav from '@services/dav';
import * as nativex from '@native';

import type { ICluster } from '@typings';

type Category = {
  name: string;
  icon: Component;
  link?: string;
  click?: () => void;
  if?: () => boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'Explore',
  setup(__props) {

const loading = ref(0);
const isNative = nativex.has();
const containerRef = useTemplateRef<HTMLDivElement>('container');
const abort = useAbort();

const recognize = ref<ICluster[] | null>(null);
const facerecognition = ref<ICluster[] | null>(null);
const places = ref<ICluster[] | null>(null);
const tags = ref<ICluster[] | null>(null);

useRouteState({
  state: { recognize, facerecognition, places, tags },
  scroll: { containerRef },
});

const loaded = reactive({
  recognize: false,
  facerecognition: false,
  places: false,
  tags: false,
});

const categories = ref([
  {
    name: t('memories', 'Folders'),
    icon: markRaw(FolderIcon),
    link: '/folders',
  },
  {
    name: t('memories', 'Favorites'),
    icon: markRaw(StarIcon),
    link: '/favorites',
  },
  {
    name: t('memories', 'Videos'),
    icon: markRaw(VideoIcon),
    link: '/videos',
  },
  {
    name: t('memories', 'Panoramas'),
    icon: markRaw(PanoramaIcon),
    link: '/panoramas',
  },
  {
    name: t('memories', 'Archive'),
    icon: markRaw(ArchiveIcon),
    link: '/archive',
  },
  {
    name: t('memories', 'On this day'),
    icon: markRaw(CalendarIcon),
    link: '/thisday',
  },
  {
    name: t('memories', 'Map'),
    icon: markRaw(MapIcon),
    link: '/map',
  },
  {
    name: t('memories', 'Settings'),
    icon: markRaw(CogIcon),
    link: undefined,
    click: _m.modals.showSettings,
    if: () => windowDims.isMobile,
  },
] as Category[]);

async function load<T>(fun: (signal: AbortSignal) => Promise<T>) {
  const signal = abort.signal;
  try {
    loading.value++;
    return await fun(signal);
  } catch (e) {
    if (isAbortError(e)) return;
    console.error(e);
  } finally {
    loading.value--;
  }
}

async function getRecognize(signal: AbortSignal) {
  recognize.value = await dav.getFaceList('recognize', { signal });
}

async function getFaceRecognition(signal: AbortSignal) {
  facerecognition.value = await dav.getFaceList('facerecognition', { signal });
}

async function getPlaces(signal: AbortSignal) {
  places.value = await dav.getPlaces({ signal });
}

async function getTags(signal: AbortSignal) {
  tags.value = (await dav.getTags({ signal })).sort((a, b) => b.count - a.count);
}

function maybeLoad() {
  if (config.recognize_enabled && !loaded.recognize) {
    loaded.recognize = true;
    if (recognize.value === null) {
      recognize.value = dav.getPlaceholderClusters(10);
    }
    load(getRecognize);
  }

  if (config.facerecognition_enabled && !loaded.facerecognition) {
    loaded.facerecognition = true;
    if (facerecognition.value === null) {
      facerecognition.value = dav.getPlaceholderClusters(10);
    }
    load(getFaceRecognition);
  }

  if (config.places_gis > 0 && !loaded.places) {
    loaded.places = true;
    if (places.value === null) {
      places.value = dav.getPlaceholderClusters(10);
    }
    load(getPlaces);
  }

  if (config.systemtags_enabled && !loaded.tags) {
    loaded.tags = true;
    if (tags.value === null) {
      tags.value = dav.getPlaceholderClusters(10);
    }
    load(getTags);
  }
}

onMounted(() => {
  maybeLoad();

  // Server copy may differ from cache; load newly enabled sections.
  watch(config, maybeLoad);

  // Remove categories that should not be shown
  categories.value = categories.value.filter((c) => !c.if || c.if());
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)('memories', 'Explore')), 1),
      (_unref(isNative))
        ? (_openBlock(), _createBlock(Searchbar, {
            key: 0,
            class: "searchbar"
          }))
        : _createCommentVNode("", true),
      (recognize.value?.length)
        ? (_openBlock(), _createBlock(ClusterHList, {
            key: 1,
            "state-key": "recognize",
            title: _unref(t)('memories', 'Recognize'),
            link: "/recognize",
            clusters: recognize.value,
            circle: true
          }, null, 8, ["title", "clusters"]))
        : _createCommentVNode("", true),
      (facerecognition.value?.length)
        ? (_openBlock(), _createBlock(ClusterHList, {
            key: 2,
            "state-key": "facerecognition",
            title: _unref(t)('memories', 'Face Recognition'),
            link: "/facerecognition",
            clusters: facerecognition.value,
            circle: true
          }, null, 8, ["title", "clusters"]))
        : _createCommentVNode("", true),
      (places.value?.length)
        ? (_openBlock(), _createBlock(ClusterHList, {
            key: 3,
            "state-key": "places",
            title: _unref(t)('memories', 'Places'),
            link: "/places",
            clusters: places.value
          }, null, 8, ["title", "clusters"]))
        : _createCommentVNode("", true),
      (tags.value?.length)
        ? (_openBlock(), _createBlock(ClusterHList, {
            key: 4,
            "state-key": "systemtags",
            title: _unref(t)('memories', 'Tags'),
            link: "/tags",
            clusters: tags.value
          }, null, 8, ["title", "clusters"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (category) => {
          return (_openBlock(), _createBlock(ExploreButton, {
            key: category.name,
            to: category.link,
            icon: category.icon,
            label: category.name,
            onClick: ($event: any) => (category.click?.())
          }, null, 8, ["to", "icon", "label", "onClick"]))
        }), 128))
      ])
    ])
  ], 512))
}
}

})